import React, { useRef, useState } from "react";
import Webcam from "react-webcam";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUserFriends, faChevronDown, faChevronRight, faCamera, faRotateRight, faUser
} from "@fortawesome/free-solid-svg-icons";
import { primaryColor } from "../../../../../../utils/comon";
import apiServices from "../../../../../../services/apiService";
import { toast } from "react-toastify";
import DeleteModal from "../../../../../common/commonmodal/DeleteModal";

const PURPOSE_OPTIONS = [
  "Meeting", "Delivery", "Interview", "Personal", "Client Visit",
  "Maintenance", "Inspection", "Event", "Other"
];

const VisitorIdentification = ({ formData, onUpdate, onNext }) => {
  const primary = primaryColor();
  const webcamRef = useRef(null);

  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [isBlockedModalOpen, setIsBlockedModalOpen] = useState(false);
  const [checking, setChecking] = useState(false);

  const checkVisitorByMobile = async (mobile) => {
    try {
      setChecking(false);
      const response = await apiServices({
        method: "GET",
        url: `/Visitor/getAllVisitorByMobileNumber/${mobile}`
      });
      const latestVisitor = response?.data?.data?.[0];
      if ((response?.success || response?.data?.isSuccess) && latestVisitor) {
        const isBlocked = latestVisitor?.status?.toLowerCase() === "block" || 
          latestVisitor?.status?.toLowerCase() === "blocked" ||
          latestVisitor?.isActive?.toLowerCase() === "inactive";
        
        if (isBlocked) {
          setIsBlockedModalOpen(true);
        } else {
          onUpdate({
            visitorName: latestVisitor.visitorName || "",
            emailId: latestVisitor.emailId || "",
            address: latestVisitor.location || "",
            purposeOfVisit: latestVisitor.purposeOfVisit || "",
            additionalCount: latestVisitor.additionalCount || 0,
            image: latestVisitor.image || null
          });
          toast.info("Details auto-filled from previous check-in.");
        }
      }
    } catch (error) {
      console.error("Error checking walkin visitor by mobile:", error);
    }
  };

  const handleMobileChange = async (e) => {
    const value = e.target.value;
    const cleanedValue = value.replace(/\D/g, '').slice(0, 10);
    onUpdate({ mobileNumber: cleanedValue });
    if (cleanedValue.length === 10) {
      await checkVisitorByMobile(cleanedValue);
    }
  };

  const handleCloseBlockedModal = () => {
    setIsBlockedModalOpen(false);
    onUpdate({
      visitorName: "",
      mobileNumber: "",
      emailId: "",
      address: "",
      purposeOfVisit: "",
      additionalCount: 0,
      image: null
    });
  };

  const openCamera = () => {
    setIsCameraOpen(true);
    onUpdate({ image: null });
  };

  const handleCapture = () => {
    const imageSrc = webcamRef.current.getScreenshot();
    if (imageSrc) {
      onUpdate({ image: imageSrc });
      setIsCameraOpen(false);
    }
  };

  const retake = () => {
    onUpdate({ image: null });
    setIsCameraOpen(true);
  };

  const hasPhoto = !!formData.image;

  return (
    <div className="flex-1 flex flex-col h-full bg-[#F6F6F4] overflow-x-hidden">
      <div className="flex-1 overflow-y-auto overflow-x-hidden px-5 pt-8 pb-4">
        {/* Header */}
        <p className="text-2xl font-black tracking-tight leading-none text-center cursor-pointer" 
           style={{ color: primary }}
           onClick={() => window.open("https://myhello.in/", "_blank")}
        >
          Hello!
        </p>
        <p className="text-xs font-bold text-[#410004] mt-4">Visitor Check-In</p>
        <h1 className="text-lg font-semibold text-[#191C1D] mt-1">
          Tell us <span style={{ color: primary }}>who you are</span>
        </h1>
        <p className="text-xs text-[#64748B] font-normal mt-1">
          Provide your details for building access.
        </p>

        {/* Custom Stepper Dots */}
        <div className="flex items-center gap-1.5 mt-4">
          <div className="w-8 h-2 rounded-full" style={{ backgroundColor: primary }} />
          <div className="w-2 h-2 rounded-full bg-gray-200" />
        </div>
        <h3 className="text-xs font-semibold text-[#191C1D] mt-2 mb-4">Complete your check-in</h3>

        {/* Photo Capture Section */}
        <div className="flex flex-col items-center justify-center mt-6 mb-4">
          <div className="relative">
            {isCameraOpen && (
              <div
                className="absolute inset-0 rounded-full border-2 animate-ping scale-110"
                style={{ borderColor: primary + "30" }}
              />
            )}
            <div
              className="w-32 h-32 rounded-full overflow-hidden flex items-center justify-center relative bg-white mx-auto"
              style={{
                border: `4px solid ${hasPhoto ? "#22C55E" : primary}`,
                boxShadow: hasPhoto ? "0 0 0 4px rgba(34,197,94,0.1)" : `0 0 0 4px ${primary}14`
              }}
            >
              {isCameraOpen ? (
                <Webcam
                  audio={false}
                  ref={webcamRef}
                  screenshotFormat="image/jpeg"
                  videoConstraints={{ facingMode: "user" }}
                  className="w-full h-full object-cover transform scale-x-[-1]"
                />
              ) : hasPhoto ? (
                <img src={formData.image} alt="Captured" className="w-full h-full object-cover" />
              ) : (
                <FontAwesomeIcon icon={faUser} className="text-4xl text-gray-300" />
              )}
            </div>

            {!isCameraOpen && !hasPhoto && (
              <button
                onClick={openCamera}
                className="absolute top-0 right-0 w-8 h-8 bg-white rounded-full shadow-lg flex items-center justify-center border border-gray-100"
                style={{ color: primary }}
              >
                <FontAwesomeIcon icon={faCamera} className="text-xs" />
              </button>
            )}

            {isCameraOpen && (
              <button
                onClick={handleCapture}
                className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-10 h-10 rounded-full flex items-center justify-center shadow-xl border-2 border-white active:scale-95 transition-all z-10"
                style={{ backgroundColor: primary }}
              >
                <FontAwesomeIcon icon={faCamera} className="text-white text-sm" />
              </button>
            )}
          </div>

          {hasPhoto && !isCameraOpen && (
            <button
              onClick={retake}
              className="mt-3 flex items-center gap-2 text-[12px] font-semibold text-gray-400 hover:text-gray-500 transition-colors"
            >
              <FontAwesomeIcon icon={faRotateRight} /> Retake Photo
            </button>
          )}
        </div>


        <div className="">
          {/* Mobile Number */}
          <label className="text-[9px] font-bold text-[#000000] uppercase tracking-widest">Mobile Number <span className="text-red-500">*</span></label>
          <div className="relative bg-white p-2 rounded-lg border border-gray-100 shadow-sm mb-2">
            <input
              type="tel"
              placeholder="Enter mobile number"
              className="w-full bg-transparent border-none outline-none text-xs font-semibold text-black mt-1 placeholder:font-semibold placeholder:text-gray-300"
              value={formData.mobileNumber}
              onChange={handleMobileChange}
              maxLength="10"
            />
          </div>

          {/* Full Name */}
          <label className="text-[9px] font-bold text-[#000000] uppercase tracking-widest">Full Name <span className="text-red-500">*</span></label>
          <div className="relative bg-white p-2 rounded-lg border border-gray-100 shadow-sm mb-2">
            <input
              type="text"
              placeholder="Enter full name"
              className="w-full bg-transparent border-none outline-none text-xs font-semibold text-black mt-1 placeholder:font-semibold placeholder:text-gray-300"
              value={formData.visitorName}
              onChange={(e) => onUpdate({ visitorName: e.target.value })}
            />
          </div>

          {/* Email */}
          <label className="text-[9px] font-bold text-[#000000] uppercase tracking-widest">Email Address</label>
          <div className="relative bg-white p-2 rounded-lg border border-gray-100 shadow-sm mb-2">
            <input
              type="email"
              placeholder="Enter email address"
              className="w-full bg-transparent border-none outline-none text-xs font-semibold text-black mt-1 placeholder:font-semibold placeholder:text-gray-300"
              value={formData.emailId}
              onChange={(e) => onUpdate({ emailId: e.target.value })}
            />
          </div>

          {/* Address */}
          <label className="text-[9px] font-bold text-[#000000] uppercase tracking-widest">Address <span className="text-red-500">*</span></label>
          <div className="relative bg-white p-2 rounded-lg border border-gray-100 shadow-sm mb-2">
            <input
              type="text"
              placeholder="Enter address"
              className="w-full bg-transparent border-none outline-none text-xs font-semibold text-black mt-1 placeholder:font-semibold placeholder:text-gray-300"
              value={formData.address}
              onChange={(e) => onUpdate({ address: e.target.value })}
            />
          </div>

          {/* Purpose */}
          <label className="text-[9px] font-bold text-[#000000] uppercase tracking-widest">Purpose of Visit <span className="text-red-500">*</span></label>
          <div className="relative bg-white p-2 rounded-lg border border-gray-100 shadow-sm mb-2">
            <div className="relative mt-1">
              <select
                className={`w-full bg-transparent border-none outline-none text-xs appearance-none ${formData.purposeOfVisit ? "text-black font-bold" : "text-gray-300 font-medium"}`}
                value={formData.purposeOfVisit}
                onChange={(e) => onUpdate({ purposeOfVisit: e.target.value })}
              >
                <option value="" disabled>Select Purpose</option>
                {PURPOSE_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
              </select>
              <FontAwesomeIcon icon={faChevronDown} className="absolute right-0 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none text-xs" />
            </div>
          </div>

          {/* Additional Visitors */}
          <label className="text-[9px] font-bold text-[#000000] uppercase tracking-widest">Additional Visitors</label>
          <div className="relative bg-white p-2 rounded-lg border border-gray-100 shadow-sm mb-2">
            <div className="flex items-center gap-2 mt-1">
              <input
                type="number"
                placeholder="0"
                className="w-full bg-transparent border-none outline-none text-xs font-semibold text-black mt-1 placeholder:font-semibold placeholder:text-gray-300"
                value={formData.additionalCount}
                onChange={(e) => onUpdate({ additionalCount: e.target.value })}
              />
              <FontAwesomeIcon icon={faUserFriends} className="text-gray-200" />
            </div>
          </div>
        </div>
      </div>

      {/* Footer Button */}
      <div className="px-2 py-3  bg-white border-t border-gray-50">
        <button
          onClick={onNext}
          className="w-full py-2 rounded-lg text-white text-xs font-black flex items-center justify-center gap-2 active:scale-95 transition-all shadow-lg"
          style={{ backgroundColor: primary }}
        >
          Select Unit <FontAwesomeIcon icon={faChevronRight} />
        </button>
      </div>

      {/* Blocked Visitor Warning Modal */}
      <DeleteModal
          isOpen={isBlockedModalOpen}
          onClose={handleCloseBlockedModal}
          onConfirm={handleCloseBlockedModal}
          title="Visitor Blocked"
          description="This visitor has been blacklisted/blocked and is not allowed to register or enter the premises. Please contact the administrator."
          confirmBtnText="OK"
          cancelBtnText="Close"
          isLoading={checking}
      />
    </div>
  );
};

export default VisitorIdentification;
